import React from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * Live password strength meter.
 * Scoring (max 6):
 *   +1 length >= 8, +1 length >= 12
 *   +1 has lowercase, +1 has uppercase, +1 has number, +1 has symbol
 *
 * Score buckets → label + color:
 *   0-1  Too short (red)
 *   2    Weak      (red-orange)
 *   3    Fair      (amber)
 *   4    Good      (yellow-green)
 *   5-6  Strong    (green)
 */
export const scorePassword = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return score;
};

const LEVELS = [
  { min: 0, label: "Too short", color: "bg-red-500", text: "text-red-400" },
  { min: 2, label: "Weak",      color: "bg-orange-500", text: "text-orange-400" },
  { min: 3, label: "Fair",      color: "bg-amber-400", text: "text-amber-300" },
  { min: 4, label: "Good",      color: "bg-lime-400", text: "text-lime-300" },
  { min: 5, label: "Strong",    color: "bg-[#00E676]", text: "text-[#00E676]" },
];

const levelFor = (score) => {
  let picked = LEVELS[0];
  for (const l of LEVELS) if (score >= l.min) picked = l;
  return picked;
};

const PasswordStrengthMeter = ({ password }) => {
  const score = scorePassword(password);
  const shown = password ? password.length > 0 : false;
  const level = levelFor(score);
  // 4 segment bars; fill proportional to score
  const filledSegments = Math.min(4, Math.max(0, Math.ceil((score / 6) * 4)));

  return (
    <div
      className={`mt-2 transition-opacity ${shown ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      aria-live="polite"
      data-testid="password-strength-meter"
    >
      <div className="flex items-center gap-1.5" data-testid="password-strength-bars">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i < filledSegments ? level.color : "bg-white/[0.08]"
            }`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
        {score >= 4 ? (
          <ShieldCheck size={11} className={level.text} />
        ) : (
          <ShieldAlert size={11} className={level.text} />
        )}
        <span className={`${level.text} font-semibold`} data-testid="password-strength-label">
          {level.label}
        </span>
        <span className="text-slate-400">
          {score < 4 ? "· mix upper/lower, numbers, symbols" : "· nice and secure"}
        </span>
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
